import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/AppLayout";
import { ImpactBadge, RelevanceBar } from "../lib/ui";
import {
  AlertTriangle, ClipboardList, Bell, CalendarClock, ListChecks, ShieldAlert, Sparkles, ArrowRight,
} from "lucide-react";

const KPIS = [
  { key: "high_impact", label: "Aggiornamenti ad alto impatto", icon: AlertTriangle, color: "#DC2626" },
  { key: "pending_assessments", label: "Valutazioni da completare", icon: ClipboardList, color: "#EA580C" },
  { key: "client_notifications", label: "Notifiche cliente", icon: Bell, color: "#D97706" },
  { key: "effective_90", label: "Requisiti efficaci ≤ 90gg", icon: CalendarClock, color: "#2563EB" },
  { key: "open_tasks", label: "Attività aperte", icon: ListChecks, color: "#0F172A" },
  { key: "overdue", label: "Attività scadute", icon: ShieldAlert, color: "#991B1B" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="text-slate-400">Caricamento...</div>;
  const firstName = (user?.name || "").split(" ")[0];

  return (
    <div className="ri-fade-in">
      <PageHeader overline={data.greeting + (firstName ? `, ${firstName}` : "")}
        title="La tua situazione regolatoria oggi"
        subtitle={`${data.site?.name || ""} — ${(data.site?.technologies || []).slice(0, 4).join(", ")}`} />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {KPIS.map((k) => (
          <div key={k.key} data-testid={`kpi-${k.key}`}
            className="rounded-xl bg-white border border-slate-200 p-4 hover:shadow-md transition-shadow">
            <k.icon size={18} style={{ color: k.color }} />
            <div className="font-head text-3xl font-bold mt-2" style={{ color: k.color }}>{data.kpi[k.key]}</div>
            <div className="text-xs text-slate-500 mt-0.5 leading-tight">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* High impact updates */}
        <div className="lg:col-span-2 rounded-xl bg-white border border-slate-200">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
            <h3 className="font-head font-semibold">Aggiornamenti ad alto impatto</h3>
            <Link to="/app/radar" className="text-xs text-blue-600 flex items-center gap-1 hover:underline">
              Radar completo <ArrowRight size={13} />
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {data.high_impact_updates.map((d) => (
              <Link key={d.id} to={`/app/radar/${d.id}`} data-testid={`dash-update-${d.id}`}
                className="block px-5 py-3 hover:bg-slate-50 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-[0.68rem] text-slate-500">{d.authority}</span>
                      <ImpactBadge level={d.impact_rating} small />
                    </div>
                    <div className="text-sm font-medium text-slate-800 leading-snug">{d.title_official}</div>
                  </div>
                  <div className="w-28"><RelevanceBar score={d.relevance_score} /></div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* AI priorities + tasks */}
        <div className="space-y-6">
          <div className="rounded-xl bg-white border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
              <Sparkles size={16} className="text-blue-600" />
              <h3 className="font-head font-semibold text-sm">Priorità suggerite dalla AI</h3>
            </div>
            <div className="p-3 space-y-2">
              {data.ai_priorities.map((p, i) => (
                <div key={i} data-testid={`ai-priority-${i}`} className="flex items-start gap-2 p-2 rounded-lg hover:bg-slate-50">
                  <ImpactBadge level={p.impact} small />
                  <div className="text-xs text-slate-700 flex-1">
                    {p.reg_id ? <Link to={`/app/radar/${p.reg_id}`} className="hover:underline">{p.text}</Link> : p.text}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl bg-white border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-head font-semibold text-sm">Le mie attività</h3>
              <Link to="/app/tasks" className="text-xs text-blue-600 hover:underline">Tutte</Link>
            </div>
            <div className="p-3 space-y-1">
              {data.my_tasks.map((t) => (
                <Link to="/app/tasks" key={t.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50">
                  <ImpactBadge level={t.priority} small />
                  <span className="text-xs text-slate-700 flex-1 truncate">{t.title}</span>
                  <span className="text-[0.65rem] font-mono text-slate-400">{(t.due_date || "").slice(0, 10)}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
